(() => {
  // Build-time copy: each PDP owns its lifecycle; no cross-product runtime dependency.
  function createDisclosureMotion({ signal, motion, openClass, onChange = () => {} }) {
    const running = new Map();
    const pending = new Map();
    let frame = null;
    const naturalHeight = panel => {
      const style = getComputedStyle(panel);
      return panel.getBoundingClientRect().height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
    };
    function settle(panel, open) {
      const record = running.get(panel);
      running.delete(panel);
      record?.animation?.cancel();
      panel.hidden = !open;
      panel.inert = !open;
      panel.setAttribute('aria-hidden', String(!open));
      if (openClass) panel.classList.toggle(openClass, open);
      record?.wrapper.replaceWith(panel);
    }
    function flush() {
      frame = null;
      // Read all starting frames before changing either side of an accordion group.
      const changes = Array.from(pending, ([panel, open]) => ({
        panel, open, previous: running.get(panel),
        start: running.has(panel) ? running.get(panel).wrapper.getBoundingClientRect().height : panel.hidden ? 0 : naturalHeight(panel)
      }));
      pending.clear();
      changes.forEach(change => {
        const { panel, previous, start } = change;
        const wrapper = previous?.wrapper || document.createElement('div');
        change.wrapper = wrapper;
        if (!previous) {
          wrapper.dataset.pdpDisclosureMotion = '';
          wrapper.style.cssText = 'display:flow-root;flex-shrink:0;align-self:stretch;width:100%;min-width:0;overflow:hidden;overflow-anchor:none;contain:layout style paint;';
          panel.before(wrapper);
          wrapper.append(panel);
        }
        wrapper.style.height = `${start}px`;
        previous?.animation?.cancel();
        panel.hidden = false;
        panel.style.removeProperty('display');
        if (openClass) panel.classList.add(openClass);
      });
      // Padding and text stay at their natural size inside a single clipped area.
      changes.forEach(change => { change.end = change.open ? naturalHeight(change.panel) : 0; });
      changes.forEach(({ panel, open, wrapper, start, end }) => {
        const record = { wrapper, open, animation: null };
        running.set(panel, record);
        if (motion.matches || typeof wrapper.animate !== 'function' || Math.abs(start - end) < 0.5) {
          settle(panel, open);
          return;
        }
        record.animation = wrapper.animate([{ height: `${start}px` }, { height: `${end}px` }], {
          duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: 'both'
        });
        record.animation.onfinish = () => {
          if (running.get(panel) !== record) return;
          settle(panel, record.open);
          onChange();
        };
      });
      onChange();
    }
    function set(panel, open, animate = true) {
      if (!panel || signal.aborted) return;
      panel.inert = !open;
      panel.setAttribute('aria-hidden', String(!open));
      if (!animate || motion.matches || typeof panel.animate !== 'function') {
        pending.delete(panel);
        settle(panel, open);
        onChange();
        return;
      }
      if (!running.has(panel) && !pending.has(panel) && panel.hidden === !open) return;
      pending.set(panel, open);
      if (frame === null) frame = requestAnimationFrame(flush);
      onChange();
    }
    function finishAll() {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      const finalStates = new Map(Array.from(running, ([panel, record]) => [panel, record.open]));
      pending.forEach((open, panel) => finalStates.set(panel, open));
      pending.clear();
      finalStates.forEach((open, panel) => settle(panel, open));
      onChange();
    }
    motion.addEventListener?.('change', finishAll, { signal });
    signal.addEventListener('abort', finishAll, { once: true });
    return { set, finishAll };
  }

  const elementName = 'pyp-photo-configurator';
  if (customElements.get(elementName)) return;

  class PypPhotoConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.variantSelect = this.querySelector('[data-pyp-variant-select]');
      const variantsNode = this.querySelector('[data-pyp-variants]');
      if (!(this.variantSelect instanceof HTMLSelectElement) || !variantsNode) return;

      try {
        const parsedVariants = JSON.parse(variantsNode.textContent || '[]');
        this.variants = Array.isArray(parsedVariants) ? parsedVariants : [];
      } catch (error) {
        this.variants = [];
      }
      if (!this.variants.length) return;

      this.controller = new AbortController();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.disclosureMotion = createDisclosureMotion({ signal: this.controller.signal, motion: this.motion });
      this.orientation = this.orientationFromUrl();
      this.orientationInputs = Array.from(this.querySelectorAll('[data-pyp-orientation-input]'));
      this.radios = Array.from(this.querySelectorAll('[data-pyp-option-index]'));
      this.dimensionPicker = this.querySelector('[data-pyp-dimension-picker]');
      this.widthSelect = this.querySelector('[data-pyp-width-select]');
      this.heightSelect = this.querySelector('[data-pyp-height-select]');
      this.dialogs = Array.from(this.querySelectorAll('.pyp-photo-dialog'));
      this.expanded = false;
      this.internalSelectChange = false;
      this.sizeChoices = this.radios
        .filter(radio => Number(radio.dataset.pypOptionIndex) === 2)
        .map(radio => this.parseSize(radio.value))
        .filter(Boolean);

      this.bindOptionInputs();
      this.bindDimensionPicker();
      this.bindExpansion();
      this.bindDialogs();
      this.orientationInputs.forEach(input => input.addEventListener('change', () => {
        if (input.checked) this.setOrientation(input.value, true);
      }, { signal: this.controller.signal }));
      this.motion.addEventListener('change', () => this.setExpanded(this.expanded, false), { signal: this.controller.signal });
      window.addEventListener('popstate', () => {
        this.setOrientation(this.orientationFromUrl(), false);
        const id = new URL(window.location.href).searchParams.get('variant');
        const variant = this.findVariantById(id);
        if (variant) this.applyVariant(variant, true);
      }, { signal: this.controller.signal });

      this.setOrientation(this.orientation, false, false);
      const initialVariant = this.findVariantById(this.variantSelect.value) || this.firstAvailableVariant();
      if (initialVariant) this.applyVariant(initialVariant, false);

      this.classList.add('is-enhanced');
      this.variantSelect.tabIndex = -1;
      this.variantSelect.setAttribute('aria-hidden', 'true');
      this.updateAvailability();
      this.setExpanded(false, false);
      this.enhanceDimensionMenus();
    }

    bindOptionInputs() {
      const { signal } = this.controller;

      this.radios.forEach(radio => {
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          const changedIndex = Number(radio.dataset.pypOptionIndex);
          const nextVariant = this.resolveVariant(changedIndex, radio.value);
          if (nextVariant) this.applyVariant(nextVariant, true);
        }, { signal });
      });

      this.variantSelect.addEventListener('change', () => {
        if (this.internalSelectChange) return;
        const variant = this.findVariantById(this.variantSelect.value);
        if (variant) this.applyVariant(variant, false);
      }, { signal });
    }

    bindDimensionPicker() {
      if (!(this.widthSelect instanceof HTMLSelectElement) || !(this.heightSelect instanceof HTMLSelectElement)) return;

      const { signal } = this.controller;
      this.populateWidths();
      this.syncDimensions(this.selectedValues()[2]);

      this.widthSelect.addEventListener('change', () => {
        this.populateHeights(this.widthSelect.value, this.heightSelect.value);
        this.selectSizeFromDimensions();
      }, { signal });

      this.heightSelect.addEventListener('change', () => {
        this.selectSizeFromDimensions();
      }, { signal });
    }

    bindExpansion() {
      const { signal } = this.controller;
      this.querySelectorAll('[data-pyp-expansion-toggle]').forEach(button => {
        button.addEventListener('click', () => this.setExpanded(!this.expanded), { signal });
      });
    }

    bindDialogs() {
      const { signal } = this.controller;

      this.querySelectorAll('[data-pyp-dialog-open]').forEach(button => {
        button.addEventListener('click', event => {
          // The Preferred help sits inside a radio label; help must not select it.
          event.preventDefault();
          const dialogId = button.dataset.pypDialogOpen;
          const dialog = dialogId ? document.getElementById(dialogId) : null;
          if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') return;

          dialog.__pypOpener = button;
          this.lockPageScroll();
          window.MyArtModalMotion ? window.MyArtModalMotion.open(dialog) : dialog.showModal();
          const surface = dialog.querySelector('[data-pyp-dialog-surface]');
          surface?.scrollTo?.(0, 0);
          requestAnimationFrame(() => {
            const closeButton = dialog.querySelector('[data-pyp-dialog-close]');
            if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
            else if (surface instanceof HTMLElement) surface.focus({ preventScroll: true });
          });
        }, { signal });
      });

      this.dialogs.forEach(dialog => {
        dialog.querySelectorAll('[data-pyp-dialog-close]').forEach(button => {
          button.addEventListener('click', () => window.MyArtModalMotion ? window.MyArtModalMotion.close(dialog) : dialog.close(), { signal });
        });

        dialog.addEventListener('cancel', event => {
          event.preventDefault();
          window.MyArtModalMotion ? window.MyArtModalMotion.close(dialog) : dialog.close();
        }, { signal });

        dialog.addEventListener('click', event => {
          if (event.target === dialog) window.MyArtModalMotion ? window.MyArtModalMotion.close(dialog) : dialog.close();
        }, { signal });

        dialog.addEventListener('close', () => {
          const opener = dialog.__pypOpener;
          delete dialog.__pypOpener;
          if (!this.dialogs.some(candidate => candidate.open)) this.unlockPageScroll();
          if (opener instanceof HTMLElement && document.contains(opener)) opener.focus({ preventScroll: true });
        }, { signal });
      });
    }

    selectedValues() {
      return [0, 1, 2].map(index => {
        const selected = this.radios.find(radio => Number(radio.dataset.pypOptionIndex) === index && radio.checked);
        return selected?.value || '';
      });
    }

    findVariantById(id) {
      return this.variants.find(variant => String(variant.id) === String(id));
    }

    firstAvailableVariant() {
      return this.variants.find(variant => variant.available) || this.variants[0];
    }

    matchesOptions(variant, values) {
      return values.every((value, index) => !value || variant.options?.[index] === value);
    }

    resolveVariant(changedIndex, changedValue) {
      const values = this.selectedValues();
      values[changedIndex] = changedValue;

      const exact = this.variants.find(variant => variant.available && this.matchesOptions(variant, values));
      if (exact) return exact;

      const candidates = this.variants.filter(variant => variant.available && variant.options?.[changedIndex] === changedValue);
      if (!candidates.length) return this.firstAvailableVariant();

      return candidates.reduce((best, candidate) => {
        const score = candidate.options.reduce((total, value, index) => {
          if (index === changedIndex) return total;
          return total + (value === values[index] ? 1 : 0);
        }, 0);
        if (!best || score > best.score) return { variant: candidate, score };
        return best;
      }, null).variant;
    }

    applyVariant(variant, notifyTheme) {
      if (!variant?.options) return;

      variant.options.slice(0, 3).forEach((value, index) => {
        const radio = this.radios.find(candidate => Number(candidate.dataset.pypOptionIndex) === index && candidate.value === value);
        if (radio) radio.checked = true;
      });

      const nextValue = String(variant.id);
      const changed = this.variantSelect.value !== nextValue;
      this.variantSelect.value = nextValue;
      this.syncDimensions(variant.options[2]);
      this.updateAvailability();
      this.setExpanded(this.expanded);

      if (notifyTheme && changed) {
        this.internalSelectChange = true;
        this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.internalSelectChange = false;
      }
      this.dispatchEvent(new CustomEvent('pyp:variant-change', { bubbles: true, detail: { variant } }));
    }

    updateAvailability() {
      const values = this.selectedValues();

      this.radios.forEach(radio => {
        const optionIndex = Number(radio.dataset.pypOptionIndex);
        const candidateValues = values.slice();
        candidateValues[optionIndex] = radio.value;
        const available = this.variants.some(variant => variant.available && this.matchesOptions(variant, candidateValues));
        const card = radio.closest('.pyp-photo-configurator__card');

        radio.disabled = !available;
        card?.classList.toggle('is-unavailable', !available);
        if (optionIndex === 0) {
          card?.classList.toggle('is-combination-unavailable', !available);
          const unavailableCopy = card?.querySelector('[data-pyp-format-unavailable-copy]');
          if (unavailableCopy) unavailableCopy.hidden = available;
        }
      });

      const unavailableDetails = this.radios.filter(radio => Number(radio.dataset.pypOptionIndex) === 1 && radio.disabled);
      const note = this.querySelector('[data-pyp-availability-note]');
      if (note) {
        if (unavailableDetails.length) {
          const colorCounts = unavailableDetails.map(radio => radio.value.match(/^\d+/)?.[0] || radio.value.split('(')[0].trim());
          const colorList = colorCounts.length === 1
            ? `${colorCounts[0]} colors`
            : colorCounts.length === 2
              ? `${colorCounts[0]} and ${colorCounts[1]} colors`
              : `${colorCounts.slice(0, -1).join(', ')}, and ${colorCounts.at(-1)} colors`;
          note.textContent = `${colorList} ${colorCounts.length === 1 ? 'is' : 'are'} not available for the selected size. Please select another size.`;
          note.hidden = false;
        } else {
          note.textContent = '';
          note.hidden = true;
        }
      }
    }

    parseSize(value) {
      if (typeof value !== 'string') return null;
      const parts = value.split('(');
      if (parts.length < 2) return null;

      const imperial = parts[0].match(/([0-9]+(?:\.[0-9]+)?)\s*"?\s*x\s*([0-9]+(?:\.[0-9]+)?)\s*"?\s*$/i);
      const metric = parts.slice(1).join('(').match(/([0-9]+(?:\.[0-9]+)?)\s*x\s*([0-9]+(?:\.[0-9]+)?)\s*cm/i);
      if (!imperial || !metric) return null;

      return {
        value,
        widthIn: imperial[1],
        heightIn: imperial[2],
        widthCm: metric[1],
        heightCm: metric[2]
      };
    }

    orientationFromUrl() {
      return new URL(window.location.href).searchParams.get('orientation') === 'horizontal' ? 'horizontal' : 'vertical';
    }

    orientedChoice(choice) {
      const horizontal = this.orientation === 'horizontal';
      const swap = horizontal ? Number(choice.widthCm) < Number(choice.heightCm) : Number(choice.widthCm) > Number(choice.heightCm);
      return swap ? { ...choice, widthCm: choice.heightCm, heightCm: choice.widthCm, widthIn: choice.heightIn, heightIn: choice.widthIn } : choice;
    }

    setOrientation(value, updateUrl, notify = true) {
      const next = value === 'horizontal' ? 'horizontal' : 'vertical';
      const changed = this.orientation !== next;
      this.orientation = next;
      this.dataset.orientation = next;
      this.orientationInputs.forEach(input => { input.checked = input.value === next; });
      const property = this.querySelector('[data-pyp-orientation-value]');
      if (property) property.value = next === 'horizontal' ? 'Horizontal' : 'Vertical';
      this.radios.filter(input => Number(input.dataset.pypOptionIndex) === 2).forEach(input => {
        const source = this.sizeChoices.find(choice => choice.value === input.value);
        if (!source) return;
        const choice = this.orientedChoice(source);
        const card = input.closest('.pyp-photo-configurator__size-card');
        card.querySelector('.pyp-photo-configurator__size-title').textContent = `(${choice.widthCm}×${choice.heightCm} cm)`;
        card.querySelector('small').textContent = `${choice.widthIn}" x ${choice.heightIn}"`;
      });
      this.closeDimensionMenus();
      this.populateWidths();
      this.syncDimensions(this.selectedValues()[2]);
      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('orientation', next);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
      if (notify && changed) this.dispatchEvent(new CustomEvent('pyp:orientation-change', { bubbles: true, detail: { orientation: next } }));
    }

    populateWidths() {
      if (!(this.widthSelect instanceof HTMLSelectElement)) return;
      const previousValue = this.widthSelect.value;
      const uniqueWidths = [];
      const seen = new Set();

      this.sizeChoices.map(choice => this.orientedChoice(choice)).forEach(choice => {
        if (seen.has(choice.widthCm)) return;
        seen.add(choice.widthCm);
        uniqueWidths.push(choice);
      });

      this.widthSelect.replaceChildren(...uniqueWidths.map(choice => {
        const option = new Option(`${choice.widthCm} cm (${choice.widthIn}")`, choice.widthCm);
        option.dataset.imperial = choice.widthIn;
        return option;
      }));

      if (uniqueWidths.some(choice => choice.widthCm === previousValue)) this.widthSelect.value = previousValue;
      this.populateHeights(this.widthSelect.value, this.heightSelect?.value || '');
    }

    populateHeights(widthCm, preferredHeight) {
      if (!(this.heightSelect instanceof HTMLSelectElement)) return;
      const choices = this.sizeChoices.map(choice => this.orientedChoice(choice)).filter(choice => choice.widthCm === widthCm);
      this.heightSelect.replaceChildren(...choices.map(choice => new Option(`${choice.heightCm} cm (${choice.heightIn}")`, choice.heightCm)));
      if (choices.some(choice => choice.heightCm === preferredHeight)) this.heightSelect.value = preferredHeight;
    }

    syncDimensions(sizeValue) {
      if (!(this.widthSelect instanceof HTMLSelectElement) || !(this.heightSelect instanceof HTMLSelectElement)) return;
      const source = this.sizeChoices.find(candidate => candidate.value === sizeValue);
      if (!source) return;
      const choice = this.orientedChoice(source);

      this.widthSelect.value = choice.widthCm;
      this.populateHeights(choice.widthCm, choice.heightCm);
      this.heightSelect.value = choice.heightCm;
      this.renderDimensionMenus();
    }

    selectSizeFromDimensions() {
      if (!(this.widthSelect instanceof HTMLSelectElement) || !(this.heightSelect instanceof HTMLSelectElement)) return;
      const choice = this.sizeChoices.map(candidate => this.orientedChoice(candidate)).find(candidate => candidate.widthCm === this.widthSelect.value && candidate.heightCm === this.heightSelect.value);
      if (!choice) return;

      const nextVariant = this.resolveVariant(2, choice.value);
      if (nextVariant) this.applyVariant(nextVariant, true);
    }

    setExpanded(expanded, animate = this.classList.contains('is-enhanced')) {
      const values = this.selectedValues();
      const standardSizes = this.sizeChoices.slice(0, 3).map(choice => choice.value);
      const locked = Number.parseInt(values[1], 10) >= 60 || !standardSizes.includes(values[2]);
      const changed = this.expanded !== Boolean(expanded || locked);
      this.expanded = Boolean(expanded || locked);
      this.classList.toggle('is-expanded', this.expanded);

      this.querySelectorAll('[data-pyp-expansion-toggle]').forEach(button => {
        button.setAttribute('aria-expanded', String(this.expanded));
        button.hidden = locked;
      });

      if (this.expanded) this.syncDimensions(this.selectedValues()[2]);
      else this.closeDimensionMenus();
      this.updateAvailability();
      const useMotion = animate && changed && !this.motion?.matches;
      this.reveal(this.querySelector('[data-pyp-extra-details]'), this.expanded, useMotion);
      this.reveal(this.querySelector('[data-pyp-size-list]'), !this.expanded, useMotion);
      this.reveal(this.dimensionPicker, this.expanded, useMotion);
    }

    reveal(panel, open, animate) {
      this.disclosureMotion.set(panel, open, animate);
    }

    enhanceDimensionMenus() {
      this.menus = [this.widthSelect, this.heightSelect].filter(Boolean).map((select, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pyp-photo-configurator__dimension-select';
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-label', select.getAttribute('aria-label'));
        const panel = document.createElement('div');
        panel.className = 'pyp-photo-configurator__dimension-menu';
        panel.id = `${this.dimensionPicker.id}-menu-${index}`;
        panel.getAnimations?.().forEach(animation => animation.cancel());
        panel.hidden = true;
        button.setAttribute('aria-controls', panel.id);
        select.hidden = true;
        select.parentElement.querySelector('[data-pyp-initial-dimension]')?.remove();
        select.after(button, panel);
        button.addEventListener('click', () => {
          const open = panel.hidden;
          this.closeDimensionMenus();
          panel.hidden = !open;
          button.setAttribute('aria-expanded', String(open));
          if (open && !this.motion.matches && typeof panel.animate === 'function') {
            panel.animate([{ opacity: 0, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 140, easing: 'ease-out' });
          }
        }, { signal: this.controller.signal });
        panel.addEventListener('keydown', event => {
          const buttons = Array.from(panel.querySelectorAll('button'));
          const current = buttons.indexOf(document.activeElement);
          const direction = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
          if (direction) {
            event.preventDefault();
            buttons[(current + direction + buttons.length) % buttons.length]?.focus();
          }
        }, { signal: this.controller.signal });
        return { select, button, panel };
      });
      document.addEventListener('click', event => {
        if (!this.dimensionPicker.contains(event.target)) this.closeDimensionMenus();
      }, { signal: this.controller.signal });
      this.dimensionPicker.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const open = this.menus.find(menu => !menu.panel.hidden);
        this.closeDimensionMenus();
        open?.button.focus();
      }, { signal: this.controller.signal });
      this.renderDimensionMenus();
    }

    closeDimensionMenus() {
      this.menus?.forEach(({ panel, button }) => {
        panel.getAnimations?.().forEach(animation => animation.cancel());
        panel.hidden = true;
        button.setAttribute('aria-expanded', 'false');
      });
    }

    renderDimensionMenus() {
      this.menus?.forEach(({ select, button, panel }) => {
        button.textContent = select.selectedOptions[0]?.textContent || '';
        panel.replaceChildren(...Array.from(select.options).map(option => {
          const choice = document.createElement('button');
          choice.type = 'button';
          choice.textContent = option.textContent;
          choice.setAttribute('aria-pressed', String(option.selected));
          choice.addEventListener('click', () => {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            this.closeDimensionMenus();
            button.focus({ preventScroll: true });
          });
          return choice;
        }));
      });
    }

    lockPageScroll() {
      if (this.pageScrollLock) return;
      const html = document.documentElement;
      const body = document.body;
      const scrollY = window.scrollY;
      const viewportGutter = Math.max(0, window.innerWidth - body.getBoundingClientRect().width);

      this.pageScrollLock = {
        html,
        body,
        scrollY,
        htmlOverflow: html.style.overflow,
        htmlOverscroll: html.style.overscrollBehavior,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyLeft: body.style.left,
        bodyRight: body.style.right,
        bodyWidth: body.style.width,
        bodyOverflow: body.style.overflow
      };

      html.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';
      // Keep the desktop document and its sticky gallery in the same scroll coordinate system.
      body.style.width = `calc(100% - ${viewportGutter}px)`;
      if (!window.matchMedia('(min-width: 750px)').matches) {
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.overflow = 'hidden';
      }
    }

    unlockPageScroll() {
      const state = this.pageScrollLock;
      if (!state) return;

      state.html.style.overflow = state.htmlOverflow;
      state.html.style.overscrollBehavior = state.htmlOverscroll;
      state.body.style.position = state.bodyPosition;
      state.body.style.top = state.bodyTop;
      state.body.style.left = state.bodyLeft;
      state.body.style.right = state.bodyRight;
      state.body.style.width = state.bodyWidth;
      state.body.style.overflow = state.bodyOverflow;
      window.scrollTo(0, state.scrollY);
      delete this.pageScrollLock;
    }

    disconnectedCallback() {
      this.querySelectorAll('dialog').forEach(dialog => { window.MyArtModalMotion?.cancel(dialog); if (dialog.open) dialog.close(); });
      this.disclosureMotion?.finishAll();
      this.querySelectorAll('[data-pyp-extra-details], [data-pyp-size-list], [data-pyp-dimension-picker]').forEach(panel => {
        ['height', 'margin-top', 'overflow'].forEach(property => panel.style.removeProperty(property));
      });
      this.dialogs?.forEach(dialog => {
        if (dialog.open) window.MyArtModalMotion ? window.MyArtModalMotion.close(dialog) : dialog.close();
      });
      this.controller?.abort();
      this.menus?.forEach(({ select, button, panel }) => { select.hidden = false; button.remove(); panel.remove(); });
      this.menus = null;
      this.variantSelect?.removeAttribute('tabindex');
      this.variantSelect?.removeAttribute('aria-hidden');
      this.unlockPageScroll();
      delete this.controller;
    }
  }

  customElements.define(elementName, PypPhotoConfigurator);
})();
